import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { EmptyState } from "../EmptyState";
import { Users, Sparkles, FileText, CalendarClock } from "lucide-react";

const APP_STATES = ["submetida", "em_analise", "entrevista", "aceite", "rejeitada"];
const APP_LABEL = {
  submetida: "Submetida",
  em_analise: "Em análise",
  entrevista: "Entrevista",
  aceite: "Aceite",
  rejeitada: "Rejeitada",
};

export const matchColor = (s) =>
  s >= 70 ? "bg-emerald-100 text-emerald-700" : s >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700";

function formatInterviewDate(iso) {
  if (!iso) return null;
  const d = new Date(iso);
  return d.toLocaleString("pt-PT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
}

/** Candidaturas recebidas pela empresa, com match IA, CV e mudança de estado. */
export const ApplicationsPanel = ({ apps, screening, matching, onBulkScreen, onComputeMatch, onViewCv, onStatusChange, onScheduleInterview }) => {
  if (apps.length === 0) {
    return (
      <EmptyState icon={Users} title="Sem candidaturas"
        description="Quando candidatos se candidatarem às tuas vagas, aparecem aqui." />
    );
  }
  return (
    <>
      {apps.some((a) => a.match_score == null) && (
        <div className="flex justify-end mb-4">
          <Button data-testid="bulk-screen-btn" size="sm" className="rounded-full bg-amber-500 hover:bg-amber-600 text-white"
            disabled={screening} onClick={onBulkScreen}>
            <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {screening ? "A triar com IA..." : "Triagem IA em massa (até 5)"}
          </Button>
        </div>
      )}
      <div className="space-y-3">
        {apps.map((a) => (
          <div key={a.id} data-testid={`company-app-${a.id}`}
            className="bg-white rounded-xl border border-black/5 p-5 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-heading font-bold tracking-tighter text-slate-900">{a.candidate_name}</p>
              <p className="text-sm text-slate-500">{a.job_title} · {a.candidate_email}</p>
              {a.message && <p className="text-sm text-slate-600 mt-2 italic">&ldquo;{a.message}&rdquo;</p>}
              {a.match_score != null && (
                <div data-testid={`match-result-${a.id}`} className="mt-2 flex items-start gap-2">
                  <Badge className={`${matchColor(a.match_score)} hover:${matchColor(a.match_score)} font-bold shrink-0`}>
                    Match {a.match_score}%
                  </Badge>
                  {a.match_summary && <p className="text-xs text-slate-500">{a.match_summary}</p>}
                  <button data-testid={`match-recalc-${a.id}`} disabled={matching === a.id}
                    onClick={() => onComputeMatch(a.id, true)}
                    className="text-xs text-slate-500 hover:text-amber-600 underline underline-offset-2 transition-colors shrink-0">
                    {matching === a.id ? "a recalcular..." : "recalcular"}
                  </button>
                </div>
              )}
              {a.interview_scheduled_at && (
                <div data-testid={`interview-info-${a.id}`} className="mt-2 flex items-center gap-1.5 text-xs text-blue-700 bg-blue-50 border border-blue-100 rounded-full px-2.5 py-1 w-fit">
                  <CalendarClock className="w-3 h-3" />
                  <span className="font-semibold">Entrevista {formatInterviewDate(a.interview_scheduled_at)}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {a.match_score == null && (
                <Button data-testid={`match-btn-${a.id}`} size="sm" variant="outline"
                  className="rounded-full border-amber-400 text-amber-600 hover:bg-amber-50"
                  disabled={matching === a.id} onClick={() => onComputeMatch(a.id)}>
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> {matching === a.id ? "A calcular..." : "Match IA"}
                </Button>
              )}
              {a.candidate_has_cv && (
                <Button data-testid={`view-cv-btn-${a.id}`} size="sm" variant="outline" className="rounded-full"
                  onClick={() => onViewCv(a.id)}>
                  <FileText className="w-3.5 h-3.5 mr-1.5" /> Ver CV
                </Button>
              )}
              {onScheduleInterview && (
                <Button data-testid={`schedule-interview-btn-${a.id}`} size="sm" variant="outline"
                  className="rounded-full border-blue-300 text-blue-700 hover:bg-blue-50"
                  onClick={() => onScheduleInterview(a)}>
                  <CalendarClock className="w-3.5 h-3.5 mr-1.5" />
                  {a.interview_scheduled_at ? "Reagendar" : "Agendar entrevista"}
                </Button>
              )}
              <Select value={a.status} onValueChange={(v) => onStatusChange(a.id, v)}>
                <SelectTrigger data-testid={`app-status-select-${a.id}`} className="w-40 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {APP_STATES.map((s) => <SelectItem key={s} value={s}>{APP_LABEL[s]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>
    </>
  );
};
